import React, { useState } from "react";

import { useLocation } from "react-router-dom";

import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  Lock,
  Mail,
  Person,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { AuthSignup } from "../../services/api/Auth/Signup";
import { signIn } from "aws-amplify/auth";
import AuthIntegrationStrip from "./components/AuthIntegrationStrip";
import AuthTextField from "./components/AuthTextField";
import logoLogin123Zap from "../../shared/assets/images/logo-login-123zap.png";
import { purple } from "../../shared/theme/themePrimitives";
import { notify } from "../../shared/utils/utils";

function getSignupErrorMessage(returnMessage) {
  const message = String(returnMessage || "").trim();
  const lowerMessage = message.toLowerCase();

  if (message.includes("Field")) {
    return "Preencha todos os campos.";
  }

  if (message === "Username already exists") {
    return "Esse nome de usuário já existe.";
  }

  if (message === "Email already exists") {
    return "Esse e-mail já foi cadastrado.";
  }

  if (message === "Email or username already exists") {
    return "E-mail ou nome de usuário já existem.";
  }

  if (message === "Invalid credentials") {
    return "Credenciais inválidas.";
  }

  if (
    message === "Password must be at least 8 characters long" ||
    lowerMessage.includes("password must be at least 8")
  ) {
    return "A senha precisa ter no mínimo 8 caracteres.";
  }

  if (
    message === "Password must contain at least 1 number" ||
    lowerMessage.includes("password must contain at least 1 number")
  ) {
    return "A senha precisa conter pelo menos 1 número.";
  }

  if (
    message === "Password must contain at least 1 uppercase letter" ||
    lowerMessage.includes("password must contain at least 1 uppercase")
  ) {
    return "A senha precisa conter pelo menos 1 letra maiúscula.";
  }

  if (
    message === "Password must contain at least 1 lowercase letter" ||
    lowerMessage.includes("password must contain at least 1 lowercase")
  ) {
    return "A senha precisa conter pelo menos 1 letra minúscula.";
  }

  if (message === "User does not exist.") {
    return "Este usuário não existe.";
  }

  return "Não foi possível criar sua conta. Verifique os dados e tente novamente.";
}

const SignUp = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const location = useLocation();
  const messageParams = location.state ? location.state.message : "";
  const [message, setMessage] = useState(messageParams ? getSignupErrorMessage(messageParams) : "");

  const handleNotify = (returnMessage) => {
    setMessage(getSignupErrorMessage(returnMessage));
  };

  const handleUsernameChange = (event) => {
    const value = event.target.value.toLowerCase();

    if (value.length === 1 && !/^[a-zA-Z]/.test(value)) {
      notify("Alert", "O nome de usuário deve começar com uma letra.", true);
      return;
    }

    if (/[^a-z0-9-_]/.test(value)) {
      notify("Alert", "Use apenas letras, números, hífens ou underscores.", true);
      return;
    }

    if (value.length > 20) {
      notify("Alert", "O nome de usuário deve ter no máximo 20 caracteres.", true);
      return;
    }

    setUsername(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username || !email || !password || !confirmPassword) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("As senhas não estão iguais.");
      return;
    }

    setLoading(true);

    try {
      const response = await AuthSignup({
        username,
        password,
        confirmPassword,
        email,
      });

      if (response && response.accessToken) {
        // Limpa cookies indesejados
        document.cookie.split(";").forEach((cookie) => {
          const cookieName = cookie.split("=")[0].trim();
          if (!["_chatwoot_session", "cw_conversation"].includes(cookieName)) {
            document.cookie = `${cookieName}=;expires=${new Date().toUTCString()};path=/; SameSite=None; Secure`;
          }
        });

        // Armazena o token no localStorage
        const expirationTime = Date.now() + 30 * 60 * 1000;
        localStorage.setItem("expiration_time", expirationTime);
        localStorage.setItem("jwt_token", response.accessToken);
        localStorage.setItem("idToken", response.idToken);
        localStorage.setItem("refreshToken", response.refreshToken);

        await signIn({ username, password });
        sessionStorage.setItem("METHOD_LOGIN", "CREATE_USER");
        setLoading(false);
        window.location.href = "/";
      } else {
        handleNotify(response?.message);
        setLoading(false);
      }
    } catch (error) {
      handleNotify(error.message);
      setLoading(false);
    }
  };

  const usernameError = Boolean(message) && (
    message === "Por favor, preencha todos os campos." && !username
  );
  const emailError = Boolean(message) && (
    (message === "Por favor, preencha todos os campos." && !email) ||
    message === "Esse e-mail já foi cadastrado." ||
    message === "E-mail ou nome de usuário já existem."
  );
  const passwordError = Boolean(message) && (
    (message === "Por favor, preencha todos os campos." && !password) ||
    message.includes("senha") ||
    message === "Credenciais inválidas."
  );
  const confirmPasswordError = Boolean(message) && (
    message === "As senhas não estão iguais." ||
    (message === "Por favor, preencha todos os campos." && !confirmPassword)
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(430px, 1fr) minmax(420px, 1fr)",
        },
        minHeight: "100dvh",
        bgcolor: "#fff",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", sm: "center" },
          alignItems: "center",
          minHeight: "100dvh",
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 4, md: 5, lg: 7, xl: 8 },
          py: { xs: 3, sm: 4, md: 5, lg: 6 },
          overflowY: "auto",
          "@media (max-height: 760px)": {
            py: { xs: 2.5, sm: 3, md: 3 },
          },
          "@media (max-height: 680px)": {
            justifyContent: "flex-start",
            py: 2,
          },
          "@media (max-height: 560px)": {
            py: 1,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            mb: { xs: 3, md: 4 },
            "@media (max-height: 760px)": { mb: 2.5 },
            "@media (max-height: 560px)": { mb: 0.75 },
          }}
        >
          <Box
            component="img"
            src={logoLogin123Zap}
            alt="123zap"
            sx={{
              display: "block",
              width: { xs: 68, md: 74 },
              height: "auto",
              "@media (max-height: 760px)": { width: 66 },
              "@media (max-height: 560px)": { width: 48 },
            }}
          />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            minWidth: 0,
          }}
        >
          <Typography sx={titleSx}>Crie sua conta</Typography>
          <Typography sx={subtitleSx}>
            Informe seus dados para acessar a plataforma.
          </Typography>

          <AuthTextField
            label="Nome de usuário"
            required
            name="username"
            value={username}
            onChange={handleUsernameChange}
            autoFocus
            autoComplete="username"
            error={usernameError}
            helperText={usernameError ? "Informe seu nome de usuário." : undefined}
            icon={<Person />}
          />

          <AuthTextField
            label="Email"
            required
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            error={emailError}
            helperText={emailError ? (email ? message : "Informe seu e-mail.") : undefined}
            icon={<Mail />}
          />

          <AuthTextField
            label="Senha"
            required
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            error={passwordError}
            helperText={passwordError ? message : undefined}
            icon={<Lock />}
            endAdornment={
              <PasswordToggle
                visible={showPassword}
                onClick={() => setShowPassword((current) => !current)}
              />
            }
          />

          <AuthTextField
            label="Confirmar senha"
            required
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            error={confirmPasswordError}
            helperText={confirmPasswordError ? message : undefined}
            icon={<Lock />}
            endAdornment={
              <PasswordToggle
                visible={showConfirmPassword}
                onClick={() => setShowConfirmPassword((current) => !current)}
              />
            }
          />

          {message && !usernameError && !emailError && !passwordError && !confirmPasswordError ? (
            <Typography sx={helperMessageSx}>{message}</Typography>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={primaryButtonSx}
          >
            {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Criar conta"}
          </Button>

          <Typography sx={accountTextSx}>Já tem uma conta?</Typography>
          <Button
            type="button"
            variant="text"
            fullWidth
            onClick={() => {
              window.location.href = "/login";
            }}
            sx={linkButtonSx}
          >
            Entrar na minha conta
          </Button>
        </Box>
      </Box>

      <AuthBrandPanel />
    </Box>
  );
};

function PasswordToggle({ visible, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
      onClick={onClick}
      sx={{
        appearance: "none",
        display: "grid",
        placeItems: "center",
        width: 24,
        height: 24,
        mr: 1.5,
        p: 0,
        border: 0,
        borderRadius: 0,
        outline: "none",
        cursor: "pointer",
        bgcolor: "transparent",
        color: "#64748B",
        "&:hover": { bgcolor: "transparent", color: "#334155" },
        "&:focus-visible": { color: purple[600] },
        "& svg": { width: 18, height: 18 },
      }}
    >
      {visible ? <VisibilityOff /> : <Visibility />}
    </Box>
  );
}

function AuthBrandPanel() {
  return (
    <Box
      sx={{
        position: "relative",
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        minHeight: "100dvh",
        overflow: "hidden",
        px: { md: 4, lg: 8, xl: 14 },
        py: { md: 5, lg: 8 },
        color: "#fff",
        bgcolor: "#1e293b",
        "@media (max-height: 760px)": {
          px: { md: 4, lg: 7 },
          py: 4,
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 960 540"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMax slice"
        sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <Box component="g" sx={{ fill: "none", stroke: "#334155", strokeWidth: 100, opacity: 0.25 }}>
          <circle r="234" cx="196" cy="23" />
          <circle r="234" cx="790" cy="491" />
        </Box>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 672 }}>
        <Typography sx={brandTitleSx}>Lembre seus clientes pelo WhatsApp</Typography>
        <Typography sx={brandSubtitleSx}>
          Automatize lembretes, avisos e cobranças no momento certo, usando os
          dados do seu sistema para reduzir tarefas manuais e melhorar o
          acompanhamento dos seus clientes.
        </Typography>
        <AuthIntegrationStrip />
      </Box>
    </Box>
  );
}

const titleSx = {
  color: "#141827",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { xs: 28, sm: 30, lg: 32 },
  fontWeight: 800,
  lineHeight: 1.25,
  "@media (max-height: 760px)": { fontSize: 28 },
  "@media (max-height: 560px)": { fontSize: 26 },
};

const subtitleSx = {
  mt: { xs: 3, md: 4 },
  mb: 2,
  color: "#667085",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { xs: 15, lg: 16 },
  lineHeight: 1.5,
  "@media (max-height: 760px)": {
    mt: 2.5,
    mb: 1.5,
    fontSize: 15,
  },
  "@media (max-height: 560px)": {
    display: "none",
    mt: 1.25,
    mb: 1,
    fontSize: 14,
  },
};

const primaryButtonSx = {
  mt: 0.5,
  minHeight: 40,
  px: 3,
  borderRadius: "999px",
  bgcolor: purple[600],
  boxShadow: "none",
  color: "#fff",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { bgcolor: purple[700], boxShadow: "none" },
  "&:active": { bgcolor: purple[800] },
  "&.Mui-disabled": { bgcolor: "rgba(79, 70, 229, 0.42)", color: "#fff" },
  "@media (max-height: 560px)": { minHeight: 38 },
};

const accountTextSx = {
  mt: { xs: 4, md: 5.5 },
  color: "#1E293B",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
          fontSize: 14,
          textAlign: "center",
          "@media (max-height: 760px)": { mt: 3.5 },
  "@media (max-height: 560px)": { mt: 0.5 },
};

const linkButtonSx = {
  mt: 0.5,
  p: 0,
  color: purple[600],
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 13,
  fontWeight: 500,
  textTransform: "none",
  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
};

const helperMessageSx = {
  mt: -0.5,
  mb: 1,
  color: "#DC2626",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: 12,
  lineHeight: "20px",
};

const brandTitleSx = {
  color: "#f1f5f9",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { md: 32, lg: 42, xl: 48 },
  fontWeight: 700,
  lineHeight: 1,
  "@media (max-height: 760px)": { fontSize: { md: 30, lg: 38 } },
};

const brandSubtitleSx = {
  mt: 3,
  color: "#94a3b8",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontSize: { md: 14, lg: 15, xl: 16 },
  lineHeight: 1.5,
  maxWidth: 672,
  "@media (max-height: 760px)": { mt: 2, fontSize: 14 },
};

export default SignUp;
